import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Fab,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
  useTheme,
  Divider,
  LinearProgress,
  IconButton,
  Collapse,
  Fade,
  Zoom,
  Badge,
} from '@mui/material';
import {
  Add,
  Group,
  Timeline,
  Edit,
  Delete,
  WhatsApp,
  LocationOn,
  Person,
  AccessTime,
  Event,
  CheckCircle,
  Warning,

  ExpandMore,

  PlayArrow,



  Search,
  Refresh,


  Flag,
  Celebration,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../contexts/UserRoleContext';
import { supabase } from '../lib/supabaseClient';
import { enhancedRealtimeService } from '../services/EnhancedRealtimeService';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
dayjs.extend(localizedFormat);

interface ScheduleCard {
  id: string;
  title: string;
  description: string;
  location: string;
  start_time: string;
  end_time: string;
  user_group: string;
  created_by: string;
  created_at: string;
  status: 'pending' | 'in-progress' | 'completed' | 'delayed';
  priority: 'low' | 'medium' | 'high';
  assigned_to?: string;
  notes?: string;
  whatsapp_message?: string;
  actual_start_time?: string; // When user actually pressed Start
  actual_end_time?: string; // When user actually pressed Complete
}

interface EventPageProps {
  onScheduleUpdate?: (schedule: ScheduleCard) => void;
}

export default function EventPage({ onScheduleUpdate }: EventPageProps) {
  const theme = useTheme();
  const { user } = useAuth();
  const { userRole } = useUserRole();
  
  const [schedules, setSchedules] = useState<ScheduleCard[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleCard | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Enhanced real-time state
  const [realtimeStatus, setRealtimeStatus] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const realtimeRef = useRef<any>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    start_time: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
    end_time: dayjs().add(30, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
    priority: 'medium' as ScheduleCard['priority'],
    assigned_to: '',
    notes: '',
    whatsapp_message: ''
  });

  // Add state for selected group (for admin/god)
  const [selectedGroup, setSelectedGroup] = useState<string>(userRole?.user_group || '');

  // Determine if user is admin or god
  const isAdmin = userRole?.user_role === 'admin' || userRole?.user_role === 'god';

  // User groups for assignment (add 'All Groups' for admin/god)
  const allUserGroups = [
    'OS Team',
    'Show Support',
    'Show Management (Bay)',
    'Show Management (Padang)',
    'Artiste Management',
    'Log & Finance',
    'Ops. Safety and Contingencies'
  ];

  // For admin/god, allow selection of any group
  const effectiveGroup = isAdmin ? selectedGroup : userRole?.user_group;

  // Mock data for demonstration
  const mockSchedules = useMemo((): ScheduleCard[] => [], []);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('event_schedules')
        .select('*')
        .order('start_time', { ascending: true });

      if (error) {
        console.warn('Event schedules table not found, using mock data:', error);
        setSnackbar({ 
          open: true, 
          message: 'Database table not found. Using demo data. Contact admin to set up the event_schedules table.', 
          severity: 'warning' 
        });
        setSchedules(mockSchedules);
        return;
      }
      setSchedules(data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load schedules. Using demo data.', 
        severity: 'error' 
      });
      setSchedules(mockSchedules);
    }
  }, [user?.id, mockSchedules]);

  // Enhanced real-time subscription setup
  const setupRealtimeSubscription = useCallback(async () => {
    try {
      console.log('Event: Setting up real-time subscription for event_schedules');
      realtimeRef.current = await enhancedRealtimeService.subscribeToChannel({
        table: 'event_schedules',
        event: '*',
        immediateUpdate: true,
        callback: (payload) => {
          console.log('Event real-time update received:', payload);
          
          // Immediate optimistic update for instant UI response
          if (payload.eventType === 'INSERT' && payload.newRecord) {
            console.log('Event: Processing INSERT update:', payload.newRecord);
            setSchedules(prev => {
              // Check if item already exists to prevent duplicates
              const exists = prev.some(schedule => schedule.id === payload.newRecord.id);
              if (exists) {
                console.log('Event: Item already exists, skipping duplicate');
                return prev;
              }
              console.log('Event: Adding new item to schedules');
              return [payload.newRecord, ...prev];
            });
          } else if (payload.eventType === 'UPDATE' && payload.newRecord) {
            console.log('Event: Processing UPDATE update:', payload.newRecord);
            setSchedules(prev => prev.map(schedule => 
              schedule.id === payload.newRecord.id ? payload.newRecord : schedule
            ));
          } else if (payload.eventType === 'DELETE' && payload.oldRecord) {
            console.log('Event: Processing DELETE update:', payload.oldRecord);
            setSchedules(prev => prev.filter(schedule => schedule.id !== payload.oldRecord.id));
          }
          
          // Clear optimistic update after 200ms for faster cleanup
          setTimeout(() => {
            // setOptimisticUpdates(prev => { // Removed unused variable
            //   const newSet = new Set(prev);
            //   if (payload.newRecord) newSet.delete(payload.newRecord.id);
            //   if (payload.oldRecord) newSet.delete(payload.oldRecord.id);
            //   return newSet;
            // });
          }, 200); // Reduced from 500ms to 200ms
        }
      });
      
      console.log('Event: Real-time subscription setup completed');
      setRealtimeStatus('connected');
    } catch (error) {
      console.error('Error setting up real-time subscription:', error);
      setRealtimeStatus('disconnected');
    }
  }, []);

  // Listen for real-time events
  useEffect(() => {
    const handleRealtimeEvent = (event: CustomEvent) => {
      const { table, updates } = event.detail;
      if (table === 'event_schedules') {
        console.log('Processing real-time updates for event_schedules:', updates);
        // Additional processing if needed
      }
    };

    window.addEventListener('realtime-insert', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-update', handleRealtimeEvent as EventListener);
    window.addEventListener('realtime-delete', handleRealtimeEvent as EventListener);

    return () => {
      window.removeEventListener('realtime-insert', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-update', handleRealtimeEvent as EventListener);
      window.removeEventListener('realtime-delete', handleRealtimeEvent as EventListener);
    };
  }, []);

  const saveSchedule = async () => {
    if (!user || !userRole?.user_group) {
      setSnackbar({ 
        open: true, 
        message: 'User information not available. Please refresh the page.', 
        severity: 'error' 
      });
      return;
    }

    const scheduleData = {
      ...formData,
      user_group: userRole.user_group,
      created_by: user.id,
      created_at: new Date().toISOString(),
      status: 'pending' as ScheduleCard['status'],
      id: editingSchedule?.id || crypto.randomUUID()
    };

    try {
      // Optimistic update for instant UI response
      if (editingSchedule) {
        console.log('Event: Optimistic update for editing schedule:', scheduleData);
        setSchedules(prev => prev.map(schedule => 
          schedule.id === editingSchedule.id ? { ...schedule, ...scheduleData } : schedule
        ));
      } else {
        console.log('Event: Optimistic update for new schedule:', scheduleData);
        setSchedules(prev => [scheduleData, ...prev]);
      }

      if (editingSchedule) {
        const { error } = await supabase
          .from('event_schedules')
          .update(scheduleData)
          .eq('id', editingSchedule.id);

        if (error) {
          console.warn('Database update failed, updating local state:', error);
          setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? scheduleData : s));
          setSnackbar({ open: true, message: 'Schedule updated (demo mode)', severity: 'success' });
        } else {
          console.log('Event: Database update successful');
          setSchedules(prev => prev.map(s => s.id === editingSchedule.id ? scheduleData : s));
          setSnackbar({ open: true, message: 'Schedule updated successfully!', severity: 'success' });
        }
      } else {
        const { error } = await supabase
          .from('event_schedules')
          .insert(scheduleData);

        if (error) {
          console.warn('Database insert failed, adding to local state:', error);
          setSchedules(prev => [...prev, scheduleData]);
          setSnackbar({ open: true, message: 'Schedule created (demo mode)', severity: 'success' });
        } else {
          console.log('Event: Database insert successful');
          setSchedules(prev => [...prev, scheduleData]);
          setSnackbar({ open: true, message: 'Schedule created successfully!', severity: 'success' });
        }
      }

      onScheduleUpdate?.(scheduleData);
      handleCloseDialog();
      
      // Clear optimistic update after successful save
      setTimeout(() => {
        // setOptimisticUpdates(prev => { // Removed unused variable
        //   const newSet = new Set(prev);
        //   newSet.delete(scheduleData.id);
        //   return newSet;
        // });
      }, 200); // Reduced from 500ms to 200ms
      
      // Fallback: Refresh data after a short delay to ensure UI is updated
      setTimeout(() => {
        console.log('Event: Fallback refresh of schedules');
        fetchSchedules();
      }, 1000);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSnackbar({ open: true, message: 'Failed to save schedule', severity: 'error' });
      
      // Rollback optimistic update on error
      if (editingSchedule) {
        fetchSchedules();
      } else {
        setSchedules(prev => prev.filter(schedule => schedule.id !== scheduleData.id));
      }
    }
  };

  const deleteSchedule = async (id: string) => {
    // Optimistic update for instant UI response
    const scheduleToDelete = schedules.find(s => s.id === id);
    console.log('Event: Optimistic delete for schedule:', id);
    setSchedules(prev => prev.filter(s => s.id !== id));
    
    try {
      const { error } = await supabase
        .from('event_schedules')
        .delete()
        .eq('id', id);

      if (error) {
        console.warn('Database delete failed, restoring from local state:', error);
        if (scheduleToDelete) {
          setSchedules(prev => [...prev, scheduleToDelete]);
        }
        setSnackbar({ open: true, message: `Failed to delete schedule: ${error.message}`, severity: 'error' });
      } else {
        console.log('Event: Database delete successful');
        setSnackbar({ open: true, message: 'Schedule deleted successfully!', severity: 'success' });
      }
      
      // Clear optimistic update after successful delete
      setTimeout(() => {
        // setOptimisticUpdates(prev => { // Removed unused variable
        //   const newSet = new Set(prev);
        //   newSet.delete(id);
        //   return newSet;
        // });
      }, 200); // Reduced from 500ms to 200ms
      
      // Fallback: Refresh data after a short delay to ensure UI is updated
      setTimeout(() => {
        console.log('Event: Fallback refresh of schedules after delete');
        fetchSchedules();
      }, 1000);
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setSnackbar({ open: true, message: 'Failed to delete schedule', severity: 'error' });
      
      // Rollback optimistic update on error
      if (scheduleToDelete) {
        setSchedules(prev => [...prev, scheduleToDelete]);
      }
    }
  };

  const updateStatus = async (id: string, status: ScheduleCard['status']) => {
    const now = new Date().toISOString();
    const updateData: any = { status };
    
    // Set actual start time when status changes to 'in-progress'
    if (status === 'in-progress') {
      updateData.actual_start_time = now;
    }
    
    // Set actual end time when status changes to 'completed'
    if (status === 'completed') {
      updateData.actual_end_time = now;
    }

    // Optimistic update for instant UI response
    setSchedules(prev => prev.map(s => s.id === id ? { 
      ...s, 
      status,
      actual_start_time: status === 'in-progress' ? now : s.actual_start_time,
      actual_end_time: status === 'completed' ? now : s.actual_end_time
    } : s));
    
    try {
      const { error } = await supabase
        .from('event_schedules')
        .update(updateData)
        .eq('id', id);

      const isDemo = schedules === mockSchedules;
      if (error) {
        console.warn('Database update failed, keeping local state:', error);
        setSnackbar({ open: true, message: `Status updated to ${status}${isDemo ? ' (demo mode)' : ''}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: `Status updated to ${status}`, severity: 'success' });
      }
      
      // Clear optimistic update after successful status update
      setTimeout(() => {
        // setOptimisticUpdates(prev => { // Removed unused variable
        //   const newSet = new Set(prev);
        //   newSet.delete(id);
        //   return newSet;
        // });
      }, 2000);
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
      
      // Rollback optimistic update on error
      fetchSchedules();
    }
  };



  const handleOpenDialog = (schedule?: ScheduleCard) => {
    if (schedule) {
      setEditingSchedule(schedule);
      setFormData({
        title: schedule.title,
        description: schedule.description,
        location: schedule.location,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        priority: schedule.priority,
        assigned_to: schedule.assigned_to || '',
        notes: schedule.notes || '',
        whatsapp_message: schedule.whatsapp_message || ''
      });
    } else {
      setEditingSchedule(null);
      setFormData({
        title: '',
        description: '',
        location: '',
        start_time: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
        end_time: dayjs().add(30, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
        priority: 'medium',
        assigned_to: '',
        notes: '',
        whatsapp_message: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSchedule(null);
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    const start = formatTime(startTime);
    const end = formatTime(endTime);
    return `${start} - ${end}`;
  };

  const formatTime = (time: string) => {
    if (time.includes('T')) {
      return dayjs(time).format('HH:mm:ss');
    }
    return time;
  };

  // Filtered schedules
  const filteredSchedules = useMemo(() => {
    let filtered = schedules;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(schedule =>
        schedule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.user_group.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(schedule => schedule.status === filterStatus);
    }

    // Filter by priority
    if (filterPriority !== 'all') {
      filtered = filtered.filter(schedule => schedule.priority === filterPriority);
    }

    return filtered;
  }, [schedules, searchTerm, filterStatus, filterPriority]);

  // Filtered schedules for selected group
  const userGroupSchedules = useMemo(() =>
    effectiveGroup
      ? filteredSchedules.filter(s => s.user_group === effectiveGroup)
      : filteredSchedules,
    [filteredSchedules, effectiveGroup]
  );

  const masterTimeline = useMemo(() => 
    filteredSchedules.sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [filteredSchedules]
  );

  // Group schedules by date
  function groupSchedulesByDate(schedules: ScheduleCard[]) {
    const groups: { [date: string]: ScheduleCard[] } = {};
    schedules.forEach(s => {
      const date = dayjs(s.start_time).format('YYYY-MM-DD');
      if (!groups[date]) groups[date] = [];
      groups[date].push(s);
    });
    // Sort dates descending (most recent first)
    const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    return { groups, sortedDates };
  }

  // Group schedules by date for the current timeline, with date filter
  const filteredSchedulesByDate = selectedDate
    ? (selectedTab === 0 ? userGroupSchedules : masterTimeline).filter(s => dayjs(s.start_time).isSame(selectedDate, 'day'))
    : (selectedTab === 0 ? userGroupSchedules : masterTimeline);
  const { groups: groupedSchedules, sortedDates } = groupSchedulesByDate(filteredSchedulesByDate);

  // Deduplicate schedules by id before rendering
  const dedupedGroupedSchedules = useMemo(() => {
    const deduped: { [date: string]: ScheduleCard[] } = {};
    for (const date of Object.keys(groupedSchedules)) {
      const seen = new Set<string>();
      deduped[date] = groupedSchedules[date].filter(s => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
    }
    return deduped;
  }, [groupedSchedules]);

  useEffect(() => {
    fetchSchedules();
    setupRealtimeSubscription();
    // Debug: log userRole
    // eslint-disable-next-line no-console
    console.log('userRole context:', userRole);
    
    // Cleanup on unmount
    return () => {
      if (realtimeRef.current) {
        enhancedRealtimeService.cleanup();
      }
    };
  }, [fetchSchedules, setupRealtimeSubscription]);

  // Check real-time connection status periodically
  useEffect(() => {
    const checkConnection = () => {
      const status = enhancedRealtimeService.getConnectionStatus();
      console.log('Event: Real-time connection status:', status);
      if (status === 'disconnected') {
        console.log('Event: Real-time disconnected, attempting to reconnect');
        setupRealtimeSubscription();
      }
    };

    const interval = setInterval(checkConnection, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [setupRealtimeSubscription]);

  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 }, 
      maxWidth: 1400, 
      mx: 'auto',
      background: '#fafbfc',
      minHeight: '100vh',
      position: 'relative',
    }}>
      
      {/* NDP Banner/Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        mb: 2,
        py: 2,
        background: '#fff',
        borderRadius: 4,
        boxShadow: '0 2px 8px #ff4b5c11',
        position: 'relative',
      }}>
        <Flag sx={{ fontSize: 48, color: '#ff4b5c' }} />
        <Typography variant="h3" sx={{
          fontWeight: 'bold',
          color: '#ff4b5c',
          letterSpacing: 2,
          textShadow: '0 1px 2px #fff8',
        }}>
          National Day Parade Timeline
        </Typography>
        <Celebration sx={{ fontSize: 48, color: '#ffb400' }} />
        
        {/* Real-time Status Indicator */}
        <Badge
          badgeContent=""
          color={realtimeStatus === 'connected' ? 'success' : realtimeStatus === 'connecting' ? 'warning' : 'error'}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            '& .MuiBadge-badge': {
              width: 12,
              height: 12,
              borderRadius: '50%',
              animation: realtimeStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
            }
          }}
        >
          <Box sx={{ width: 8, height: 8 }} />
        </Badge>
      </Box>

      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            mb: 2,
            color: '#b71c1c',
            letterSpacing: 1,
            textShadow: '0 1px 2px #fff8',
          }}
        >
          Celebrate Singapore! Plan, track, and share every moment of NDP.
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, color: '#ff4b5c' }}>
          Professional event scheduling with real-time collaboration
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search schedules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="delayed">Delayed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  label="Priority"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setFilterPriority('all');
                }}
                startIcon={<Refresh />}
                sx={{ borderRadius: 2 }}
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  console.log('Event: Manual refresh triggered');
                  fetchSchedules();
                }}
                startIcon={<Refresh />}
                sx={{ 
                  borderRadius: 2,
                  background: '#ff4b5c',
                  '&:hover': { background: '#d32f2f' }
                }}
              >
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} md={1}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                minHeight: 56,
                borderRadius: 2,
                border: '1px solid',
                borderColor: realtimeStatus === 'connected' ? 'success.main' : 
                            realtimeStatus === 'connecting' ? 'warning.main' : 'error.main',
                bgcolor: realtimeStatus === 'connected' ? 'success.light' : 
                        realtimeStatus === 'connecting' ? 'warning.light' : 'error.light',
                color: realtimeStatus === 'connected' ? 'success.dark' : 
                       realtimeStatus === 'connecting' ? 'warning.dark' : 'error.dark',
              }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%',
                  bgcolor: realtimeStatus === 'connected' ? 'success.main' : 
                          realtimeStatus === 'connecting' ? 'warning.main' : 'error.main',
                  mr: 1,
                  animation: realtimeStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
                }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {realtimeStatus === 'connected' ? 'Live' : 
                   realtimeStatus === 'connecting' ? 'Connecting' : 'Offline'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Date Filter */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={setSelectedDate}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200, borderRadius: 2 } } }}
            sx={{ borderRadius: 2 }}
          />
          {selectedDate && (
            <Button onClick={() => setSelectedDate(null)} variant="outlined" sx={{ borderRadius: 2 }}>
              Clear
            </Button>
          )}
        </Box>
      </LocalizationProvider>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs 
          value={selectedTab} 
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
            }
          }}
        >
          <Tab 
            icon={<Group />} 
            label={`${isAdmin ? (selectedGroup || 'All Groups') : (userRole?.user_group || 'Your Group')} Timeline`} 
            iconPosition="start"
          />
          <Tab 
            icon={<Timeline />} 
            label="Master Timeline" 
            iconPosition="start"
          />
        </Tabs>
        {/* If admin/god, show group selector */}
        {isAdmin && selectedTab === 0 && (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>User Group</InputLabel>
              <Select
                value={selectedGroup}
                onChange={e => setSelectedGroup(e.target.value)}
                label="User Group"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Groups</MenuItem>
                {allUserGroups.map(group => (
                  <MenuItem key={group} value={group}>{group}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Timeline Content */}
      <Box sx={{ position: 'relative' }}>
        {/* Timeline line */}
        <Box
          sx={{
            position: 'absolute',
            left: 20,
            top: 0,
            bottom: 0,
            width: 4,
            background: '#ff4b5c',
            borderRadius: 2,
            opacity: 0.15,
            boxShadow: '0 0 8px 1px #ff4b5c22',
          }}
        />

        {/* Timeline Items (grouped by date) */}
        <Box sx={{ pl: 4 }}>
          {sortedDates.map(date => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                fontWeight: 'bold',
                color: '#ff4b5c',
                mb: 2,
                letterSpacing: 1,
                textShadow: '0 1px 2px #fff8',
                pl: 1,
              }}>
                {dayjs(date).format('D MMMM YYYY (dddd)')}
              </Typography>
              {dedupedGroupedSchedules[date]
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map((schedule, index) => (
                  <Fade in={true} timeout={500 + index * 100} key={schedule.id}>
                    <Card 
                      sx={{ 
                        mb: 2, 
                        borderRadius: 4,
                        boxShadow: '0 2px 8px #ff4b5c11',
                        border: '1px solid #ff4b5c22',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px #ff4b5c22',
                        },
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      {/* Timeline connector */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -20,
                          top: 0,
                          bottom: 0,
                          width: 6,
                          background: schedule.status === 'completed' ? '#43e97b' :
                                     schedule.status === 'in-progress' ? '#ffb400' :
                                     schedule.status === 'delayed' ? '#ff4b5c' :
                                     '#bdbdbd',
                          borderRadius: 3,
                          boxShadow: '0 0 8px 1px #ff4b5c22',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: -6,
                            top: 20,
                            width: 18,
                            height: 18,
                            background: schedule.status === 'completed' ? '#43e97b' :
                                         schedule.status === 'in-progress' ? '#ffb400' :
                                         schedule.status === 'delayed' ? '#ff4b5c' :
                                         '#bdbdbd',
                            borderRadius: '50%',
                            border: `4px solid #fff`,
                            boxShadow: schedule.status === 'in-progress' ? '0 0 8px 2px #ffb40044' :
                                        schedule.status === 'delayed' ? '0 0 8px 2px #ff4b5c44' :
                                        schedule.status === 'completed' ? '0 0 8px 2px #43e97b44' :
                                        '0 0 4px 1px #bdbdbd44',
                            animation: schedule.status === 'in-progress' ? 'pulse 1.5s infinite' : 'none',
                          }
                        }}
                      />
                      <CardContent sx={{ pl: 4, pr: 2, py: 3 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                                {schedule.title}
                              </Typography>
                              <Chip 
                                label={schedule.status} 
                                sx={{
                                  background: schedule.status === 'completed' ? 'linear-gradient(90deg, #43e97b 0%, #fff 100%)' :
                                               schedule.status === 'in-progress' ? 'linear-gradient(90deg, #ffb400 0%, #fff 100%)' :
                                               schedule.status === 'delayed' ? 'linear-gradient(90deg, #ff4b5c 0%, #fff 100%)' :
                                               'linear-gradient(90deg, #bdbdbd 0%, #fff 100%)',
                                  color: schedule.status === 'delayed' ? '#fff' : '#222',
                                  fontWeight: 700,
                                  fontSize: '0.95em',
                                  px: 1.5,
                                  borderRadius: 2,
                                  boxShadow: schedule.status === 'delayed' ? '0 0 8px 2px #ff4b5c44' : 'none',
                                }}
                                size="small"
                              />
                              <Chip 
                                label={schedule.priority} 
                                sx={{
                                  background: schedule.priority === 'high' ? 'linear-gradient(90deg, #ff4b5c 0%, #fff 100%)' :
                                               schedule.priority === 'medium' ? 'linear-gradient(90deg, #ffb400 0%, #fff 100%)' :
                                               'linear-gradient(90deg, #43e97b 0%, #fff 100%)',
                                  color: schedule.priority === 'high' ? '#fff' : '#222',
                                  fontWeight: 700,
                                  fontSize: '0.95em',
                                  px: 1.5,
                                  borderRadius: 2,
                                  boxShadow: schedule.priority === 'high' ? '0 0 8px 2px #ff4b5c44' : 'none',
                                }}
                                size="small"
                              />
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {schedule.description}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton 
                              size="small" 
                              onClick={() => toggleExpanded(schedule.id)}
                              sx={{ transform: expandedItems.has(schedule.id) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                            >
                              <ExpandMore />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Quick Info */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {schedule.location}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column', minHeight: 48 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatTimeRange(schedule.start_time, schedule.end_time)}
                              </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                {schedule.actual_start_time && (
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                                    Started: {dayjs(schedule.actual_start_time).format('HH:mm')}
                                  </Typography>
                                )}
                                {schedule.actual_end_time && (
                                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                                    Completed: {dayjs(schedule.actual_end_time).format('HH:mm')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Group fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {schedule.assigned_to || 'Unassigned'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Person fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {schedule.user_group}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Progress bar for in-progress items */}
                        {schedule.status === 'in-progress' && (
                          <Box sx={{ mb: 2 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={70} 
                              sx={{ 
                                height: 6, 
                                borderRadius: 3,
                                backgroundColor: theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: theme.palette.warning.main,
                                }
                              }} 
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              In Progress - 70% Complete
                            </Typography>
                          </Box>
                        )}

                        {/* Expanded Content */}
                        <Collapse in={expandedItems.has(schedule.id)}>
                          <Divider sx={{ my: 2 }} />
                          
                          {schedule.notes && (
                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                              <Typography variant="body2">{schedule.notes}</Typography>
                            </Alert>
                          )}

                          {/* Actual Times Display */}
                          {(schedule.actual_start_time || schedule.actual_end_time) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                Actual Times
                              </Typography>
                              <Grid container spacing={2}>
                                {schedule.actual_start_time && (
                                  <Grid item xs={12} sm={6}>
                                    <Alert 
                                      severity="success" 
                                      icon={<PlayArrow />}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Started at {dayjs(schedule.actual_start_time).format('MMM DD, YYYY HH:mm')}
                                      </Typography>
                                    </Alert>
                                  </Grid>
                                )}
                                {schedule.actual_end_time && (
                                  <Grid item xs={12} sm={6}>
                                    <Alert 
                                      severity="info" 
                                      icon={<CheckCircle />}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Completed at {dayjs(schedule.actual_end_time).format('MMM DD, YYYY HH:mm')}
                                      </Typography>
                                    </Alert>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )}

                          {/* Action Buttons */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              flexWrap: 'wrap',
                              mt: 2,
                              p: 1,
                              background: 'rgba(245,247,250,0.7)',
                              borderRadius: 3,
                              boxShadow: '0 2px 8px rgba(33,150,243,0.07)',
                              justifyContent: { xs: 'center', sm: 'flex-start' },
                            }}
                          >
                            <Button
                              size="medium"
                              variant="contained"
                              startIcon={<Edit />}
                              onClick={() => handleOpenDialog(schedule)}
                              sx={{
                                borderRadius: 999,
                                background: 'linear-gradient(90deg, #42a5f5 0%, #478ed1 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(66,165,245,0.15)',
                                fontWeight: 600,
                                px: 2.5,
                                '&:hover': {
                                  background: 'linear-gradient(90deg, #478ed1 0%, #42a5f5 100%)',
                                  boxShadow: '0 4px 16px rgba(66,165,245,0.18)',
                                },
                                textTransform: 'none',
                              }}
                            >
                              Edit
                            </Button>
                            {/* WhatsApp Share Button: always available, generates message based on current status */}
                            <Button
                              size="medium"
                              variant="contained"
                              startIcon={<WhatsApp />}
                              onClick={() => {
                                // Generate WhatsApp message based on current status
                                const startTime = dayjs(schedule.start_time).format('HH:mm:ss');
                                const endTime = dayjs(schedule.end_time).format('HH:mm:ss');
                                const date = dayjs(schedule.start_time).format('DD MMM YYYY');
                                let message = '';
                                if (schedule.status === 'pending') {
                                  message = `📅 *${schedule.title}*\n📍 Location: ${schedule.location}\n⏰ Scheduled: ${startTime} - ${endTime} (${date})`;
                                } else if (schedule.status === 'in-progress') {
                                  const actualStart = schedule.actual_start_time ? 
                                    `\n🚀 Actually started: ${dayjs(schedule.actual_start_time).format('HH:mm')}` : '';
                                  message = `🔄 *${schedule.title}* has STARTED!\n📍 Location: ${schedule.location}\n⏰ Scheduled: ${startTime} - ${endTime} (${date})${actualStart}`;
                                } else if (schedule.status === 'delayed') {
                                  message = `⚠️ *${schedule.title}* has been DELAYED!\n📍 Location: ${schedule.location}\n⏰ Scheduled: ${startTime} - ${endTime} (${date})${schedule.notes ? `\nReason: ${schedule.notes}` : ''}`;
                                } else if (schedule.status === 'completed') {
                                  const actualStart = schedule.actual_start_time ? 
                                    `\n🚀 Started: ${dayjs(schedule.actual_start_time).format('HH:mm')}` : '';
                                  const actualEnd = schedule.actual_end_time ? 
                                    `\n✅ Completed: ${dayjs(schedule.actual_end_time).format('HH:mm')}` : '';
                                  message = `✅ *${schedule.title}* has been COMPLETED!\n📍 Location: ${schedule.location}\n⏰ Scheduled: ${startTime} - ${endTime} (${date})${actualStart}${actualEnd}`;
                                }
                                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                                window.open(whatsappUrl, '_blank');
                              }}
                              sx={{
                                borderRadius: 999,
                                background: 'linear-gradient(90deg, #25d366 0%, #128c7e 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(37,211,102,0.15)',
                                fontWeight: 600,
                                px: 2.5,
                                '&:hover': {
                                  background: 'linear-gradient(90deg, #128c7e 0%, #25d366 100%)',
                                  boxShadow: '0 4px 16px rgba(37,211,102,0.18)',
                                },
                                textTransform: 'none',
                              }}
                            >
                              Share
                            </Button>
                            {/* Start Button: show for pending or delayed */}
                            {['pending', 'delayed'].includes(schedule.status) && (
                              <Button
                                size="medium"
                                variant="contained"
                                startIcon={<PlayArrow />}
                                onClick={async () => {
                                  await updateStatus(schedule.id, 'in-progress');
                                }}
                                sx={{
                                  borderRadius: 999,
                                  background: 'linear-gradient(90deg, #ffb347 0%, #ffcc33 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(255,179,71,0.15)',
                                  fontWeight: 600,
                                  px: 2.5,
                                  '&:hover': {
                                    background: 'linear-gradient(90deg, #ffcc33 0%, #ffb347 100%)',
                                    boxShadow: '0 4px 16px rgba(255,179,71,0.18)',
                                  },
                                  textTransform: 'none',
                                }}
                              >
                                Start
                              </Button>
                            )}
                            {/* Delay Button: show for pending or in-progress */}
                            {['pending', 'in-progress'].includes(schedule.status) && (
                              <Button
                                size="medium"
                                variant="contained"
                                startIcon={<Warning />}
                                onClick={async () => {
                                  await updateStatus(schedule.id, 'delayed');
                                }}
                                sx={{
                                  borderRadius: 999,
                                  background: 'linear-gradient(90deg, #ff5858 0%, #f09819 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(255,88,88,0.15)',
                                  fontWeight: 600,
                                  px: 2.5,
                                  '&:hover': {
                                    background: 'linear-gradient(90deg, #f09819 0%, #ff5858 100%)',
                                    boxShadow: '0 4px 16px rgba(255,88,88,0.18)',
                                  },
                                  textTransform: 'none',
                                }}
                              >
                                Delay
                              </Button>
                            )}
                            {/* Complete Button: show only if in-progress or delayed */}
                            {['in-progress', 'delayed'].includes(schedule.status) && (
                              <Button
                                size="medium"
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={async () => {
                                  await updateStatus(schedule.id, 'completed');
                                }}
                                sx={{
                                  borderRadius: 999,
                                  background: 'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)',
                                  color: '#fff',
                                  boxShadow: '0 2px 8px rgba(67,233,123,0.15)',
                                  fontWeight: 600,
                                  px: 2.5,
                                  opacity: schedule.status === 'completed' ? 0.6 : 1,
                                  '&:hover': {
                                    background: 'linear-gradient(90deg, #38f9d7 0%, #43e97b 100%)',
                                    boxShadow: '0 4px 16px rgba(67,233,123,0.18)',
                                  },
                                  textTransform: 'none',
                                }}
                              >
                                Complete
                              </Button>
                            )}
                            <Button
                              size="medium"
                              variant="contained"
                              startIcon={<Delete />}
                              onClick={() => deleteSchedule(schedule.id)}
                              sx={{
                                borderRadius: 999,
                                background: 'linear-gradient(90deg, #b24592 0%, #f15f79 100%)',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(178,69,146,0.15)',
                                fontWeight: 600,
                                px: 2.5,
                                '&:hover': {
                                  background: 'linear-gradient(90deg, #f15f79 0%, #b24592 100%)',
                                  boxShadow: '0 4px 16px rgba(178,69,146,0.18)',
                                },
                                textTransform: 'none',
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  </Fade>
                ))}
            </Box>
          ))}
        </Box>

        {/* Empty State */}
        {(selectedTab === 0 ? userGroupSchedules : masterTimeline).length === 0 && (
          <Fade in={true} timeout={1000}>
            <Card sx={{ 
              borderRadius: 3, 
              textAlign: 'center', 
              py: 8,
              background: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)'
            }}>
              <CardContent>
                <Event sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                  No schedules found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Create your first schedule to get started!'
                  }
                </Typography>
                {!searchTerm && filterStatus === 'all' && filterPriority === 'all' && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleOpenDialog()}
                    startIcon={<Add />}
                    sx={{ borderRadius: 2 }}
                  >
                    Create Schedule
                  </Button>
                )}
              </CardContent>
            </Card>
          </Fade>
        )}
      </Box>

      {/* Create Schedule Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned To Group</InputLabel>
                <Select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  label="Assigned To Group"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select a group</MenuItem>
                  {allUserGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                inputProps={{
                  step: 1
                }}
                helperText="Select date and time with seconds precision"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                inputProps={{
                  step: 1
                }}
                helperText="Select date and time with seconds precision"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as ScheduleCard['priority'] })}
                  label="Priority"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
                placeholder="Additional notes or instructions"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="WhatsApp Message Template (Optional)"
                value={formData.whatsapp_message}
                onChange={(e) => setFormData({ ...formData, whatsapp_message: e.target.value })}
                multiline
                rows={3}
                placeholder={`📅 *{title}*
📍 *Location:* {location}
⏰ *Start:* {start} ({date})
⏰ *End:* {end} ({date})
👥 *Assigned:* {assigned}
🔴 *Priority:* {priority}
📝 *Status:* {status}

{description}

📋 *Notes:* {notes}`}
                helperText="Leave empty for auto-generated message, or use placeholders: {title}, {location}, {start}, {end}, {date}, {assigned}, {priority}, {status}, {description}, {notes}"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={saveSchedule} 
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB for creating new schedule */}
      <Zoom in={true} timeout={1000}>
        <Fab
          color="primary"
          aria-label="add schedule"
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            width: 56,
            height: 56,
            boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 12px 40px rgba(33, 150, 243, 0.4)',
            }
          }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      </Zoom>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 